// PublicCalendar — read-only public schedule at /reservations.
// Mirrors the private Schedule court-column view (resourceTimeGridDay).
import React, { useEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebase';
import { useBranding } from '../contexts/BrandingContext';
import { HOUR_LABEL_INTERVAL, hourLabelOnly } from '../utils/calendarLabels';
import { windowEvents } from '../utils/schoolWindow';

const DAY_TO_NUM = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const CLASS_COLORS = { 'junior-training': '#1E3A5F', 'adult-training': '#1E6B3C', 'adult-practice': '#1E6B3C' };
const COURTS = [
  { id: 'court-1', title: 'Court 1' },
  { id: 'court-2', title: 'Court 2' },
  { id: 'court-3', title: 'Court 3' },
  { id: 'court-4', title: 'Court 4' },
  { id: 'court-5', title: 'Court 5' },
  { id: 'court-6', title: 'Court 6' },
];

function PublicCalendar() {
  const branding = useBranding();
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => onAuthStateChanged(auth, u => setSignedIn(!!u)), []);

  useEffect(() => {
    (async () => {
      try {
        const [classSnap, blockSnap, brandSnap, mbSnap] = await Promise.all([
          getDocs(collection(db, 'classes')),
          getDocs(collection(db, 'rentalTimeBlocks')),
          getDoc(doc(db, 'settings', 'branding')),
          getDocs(collection(db, 'memberBookings')),
        ]);
        const tint = brandSnap.exists() ? (brandSnap.data().primeTintColor || '#DDD2B5') : '#DDD2B5';
        const merged = new Map();
        classSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(c => c.date && c.startTime && c.endTime && c.status !== 'canceled')
          .forEach(c => {
            const title = c.eventName || `${(c.sessionType || '').replace(/-/g, ' ')}${c.skillGroup ? ` \u2014 ${c.skillGroup}` : ''}`;
            const key = `${c.date}|${c.startTime}|${c.endTime}|${title}`;
            if (merged.has(key)) { merged.get(key).courts.push(c.courtNumber || 'court-1'); return; }
            merged.set(key, { ...c, title, courts: [c.courtNumber || 'court-1'] });
          });
        const classEvents = [...merged.values()].map(c => ({
          id: c.id,
          title: c.title,
          start: `${c.date}T${c.startTime}`,
          end: `${c.date}T${c.endTime}`,
          resourceIds: [...new Set(c.courts)],
          backgroundColor: c.eventColor || CLASS_COLORS[c.sessionType] || '#1E3A5F',
          borderColor: c.eventColor || CLASS_COLORS[c.sessionType] || '#1E3A5F',
          extendedProps: { court: [...new Set(c.courts)].sort().join(', ').replace(/court-/g, 'Court '), description: c.description || '' },
        }));
        const tintEvents = blockSnap.docs
          .map(d => d.data())
          .filter(b => b.isPrime && Array.isArray(b.days) && b.startTime && b.endTime)
          .flatMap((b, i) => COURTS.map(ct => ({
            id: `prime-${i}-${ct.id}`,
            resourceId: ct.id,
            daysOfWeek: b.days.map(day => DAY_TO_NUM[day]).filter(n => n !== undefined),
            startTime: b.startTime,
            endTime: b.endTime,
            display: 'background',
            backgroundColor: tint,
          })));
        const bookingEvents = mbSnap.docs
          .map(d => d.data())
          .filter(b => b.status !== 'cancelled' && b.date && b.startTime && b.endTime)
          .flatMap((b, i) => (Array.isArray(b.courts) ? b.courts : [b.courts].filter(Boolean)).map(courtId => ({
            id: `mb-${i}-${courtId}`,
            title: 'Reserved',
            start: `${b.date}T${b.startTime}`,
            end: `${b.date}T${b.endTime}`,
            resourceIds: [courtId],
            backgroundColor: '#2E86C1',
            borderColor: '#2E86C1',
            extendedProps: { noClick: true },
          })));
        const schoolSnap = await getDoc(doc(db, 'settings', 'schoolSchedule')).catch(() => null);
        const windowBlocks = windowEvents(schoolSnap?.exists() ? schoolSnap.data() : null, COURTS.map(c => c.id));
        setEvents([...classEvents, ...tintEvents, ...bookingEvents, ...windowBlocks]);
      } catch (e) {
        console.error('Public calendar load failed:', e);
      }
    })();
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#0D1E35', p: { xs: 1, sm: 3 } }}>
      <Box sx={{ maxWidth: 1300, mx: 'auto' }}>
        <Typography variant="h5" sx={{ color: '#C9A84C', fontWeight: 700, mb: 0.5, textAlign: 'center' }}>
          {branding.facilityName}
        </Typography>
        <Typography variant="subtitle1" sx={{ color: 'white', mb: 1, textAlign: 'center' }}>
          Class & Court Schedule
        </Typography>
        {signedIn && (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Button size="small" variant="outlined" href="/dashboard"
              sx={{ color: '#C9A84C', borderColor: '#C9A84C', '&:hover': { borderColor: '#C9A84C', backgroundColor: 'rgba(201,168,76,0.1)' } }}>
              ← Back to Dashboard
            </Button>
          </Box>
        )}
        <Box sx={{ backgroundColor: 'white', borderRadius: 2, p: { xs: 1, sm: 2 }, '& .fc-bg-event': { opacity: '1 !important' } }}>
          <FullCalendar
            plugins={[resourceTimeGridPlugin, timeGridPlugin, dayGridPlugin, listPlugin]}
            schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
            initialView="resourceTimeGridDay"
            resources={COURTS}
            headerToolbar={{ left: 'prev,next today', center: 'title', right: 'resourceTimeGridDay,timeGridWeek,listWeek' }}
            buttonText={{ resourceTimeGridDay: 'day', timeGridWeek: 'week', listWeek: 'list' }}
            events={events}
            eventClick={(info) => { if (info.event.display !== 'background' && !info.event.extendedProps.noClick) setSelected(info.event); }}
            height="auto"
            slotMinTime="07:30:00"
            slotMaxTime="22:00:00"
            slotLabelInterval={HOUR_LABEL_INTERVAL}
            slotLabelContent={hourLabelOnly}
            allDaySlot={false}
            nowIndicator
          />
        </Box>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', textAlign: 'center', mt: 1.5 }}>
          Shaded areas are prime hours. Sign in at torchvolleyball.com to book courts or register for classes.
        </Typography>
      </Box>
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="xs" fullWidth>
        {selected && (<>
          <DialogTitle sx={{ backgroundColor: '#0D1E35', color: '#C9A84C', fontWeight: 700 }}>{selected.title}</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {selected.start?.toLocaleDateString()} · {selected.start?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}–{selected.end?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
            {selected.extendedProps.court && <Typography variant="body2" sx={{ mb: 1 }}>{selected.extendedProps.court}</Typography>}
            {selected.extendedProps.description && <Typography variant="body2" sx={{ color: '#666' }}>{selected.extendedProps.description}</Typography>}
          </DialogContent>
          <DialogActions><Button onClick={() => setSelected(null)}>Close</Button></DialogActions>
        </>)}
      </Dialog>
    </Box>
  );
}

export default PublicCalendar;
